import { GA_MEASUREMENT_ID, googleAnalyticsEnabled } from '@/lib/analytics/google-analytics'

/**
 * GA4 conversion events — the one typed way to send them.
 *
 * Every call is a safe no-op when gtag is absent, which is the normal case
 * almost everywhere: an environment without NEXT_PUBLIC_GA_MEASUREMENT_ID, a
 * partner host, the native shell, or any authenticated route (see
 * lib/analytics/tag-scope.ts for where the tag loads at all). Nothing here
 * decides WHETHER to measure; it only refuses to throw when it can't.
 *
 * Consent is enforced downstream of this helper, by gtag itself: Consent Mode
 * holds back cookies in the UK/EEA until the banner is accepted, and a Reject
 * sets `ga-disable-<ID>`, which gtag checks before every hit — events included.
 *
 * `send_to` pins each event to the GA4 property. The same gtag.js load also
 * carries the Ads conversion id, and an event without `send_to` fans out to
 * every configured id; Ads gets its one sign-up signal from
 * lib/analytics/google-ads.ts and nothing else.
 */

export type SignUpMethod = 'email' | 'microsoft' | 'apple'

export type AnalyticsEventParams = {
  /** A workspace was created. Fired once, from a server-set one-shot cookie (lib/analytics/signup-flag.ts). */
  sign_up: { method: SignUpMethod; plan?: string }
  /** A sign-in completed. Exists to carry `user_id` (see setAnalyticsUserId). */
  login: Record<string, never>
  /** A booking-link click (Calendly) or a successful contact-form submit. */
  generate_lead: { lead_source: 'calendly' | 'contact_form'; page_path?: string }
  /** A pricing-card CTA. */
  select_plan: { plan_name: string }
  /** First touch of a given control in an interactive homepage demo. */
  try_demo: { demo: string; control?: string }
}

export type AnalyticsEventName = keyof AnalyticsEventParams

type Gtag = (...args: unknown[]) => void

function gtagOrNull(): Gtag | null {
  if (!googleAnalyticsEnabled()) return null
  if (typeof window === 'undefined') return null
  return typeof window.gtag === 'function' ? window.gtag : null
}

/** True when an event sent now would reach gtag (it may still drop it for consent). */
export function analyticsReady(): boolean {
  return gtagOrNull() !== null
}

/** Send one GA4 event. Returns whether it was handed to gtag. Never throws. */
export function trackEvent<N extends AnalyticsEventName>(
  name: N,
  params: AnalyticsEventParams[N],
): boolean {
  const gtag = gtagOrNull()
  if (!gtag) return false
  try {
    gtag('event', name, { ...params, send_to: GA_MEASUREMENT_ID })
    return true
  } catch {
    // Analytics must never take the page down. Same contract as google-ads.ts.
    return false
  }
}

/**
 * Attach our internal user id to subsequent hits.
 *
 * Only ever the opaque `User.id` — never an email or a name: GA's terms forbid
 * PII in user_id, and gtag does not hash this field the way it hashes
 * `user_data` for Ads enhanced conversions. `send_page_view: false` because
 * re-issuing `config` would otherwise count a second page_view.
 */
export function setAnalyticsUserId(userId: string | null | undefined): boolean {
  if (!userId) return false
  const gtag = gtagOrNull()
  if (!gtag) return false
  try {
    gtag('config', GA_MEASUREMENT_ID, { user_id: userId, send_page_view: false })
    return true
  } catch {
    return false
  }
}

/** True for a link that opens our booking page (Calendly, or whatever NEXT_PUBLIC_BOOK_CALL_URL points at). */
export function isBookingLink(href: string | null | undefined, bookCallUrl = process.env.NEXT_PUBLIC_BOOK_CALL_URL): boolean {
  if (!href) return false
  let url: URL
  try {
    url = new URL(href, 'https://www.surveythanks.com')
  } catch {
    return false
  }
  const host = url.hostname.toLowerCase()
  if (host === 'calendly.com' || host.endsWith('.calendly.com')) return true
  if (!bookCallUrl) return false
  try {
    const booking = new URL(bookCallUrl)
    return url.hostname === booking.hostname && url.pathname.startsWith(booking.pathname)
  } catch {
    return false
  }
}

const seenDemoTouches = new Set<string>()

/**
 * `try_demo`, at most once per (demo, control) per page load. The demos are
 * toggles and tab bars — a visitor flipping "Signal" on and off ten times is one
 * try, not ten, and a per-click event would drown the funnel in noise.
 */
export function trackDemoOnce(demo: string, control?: string): boolean {
  const key = `${demo}\u0000${control ?? ''}`
  if (seenDemoTouches.has(key)) return false
  const sent = trackEvent('try_demo', control ? { demo, control } : { demo })
  // Only remember touches that were actually sent, so a click before gtag
  // finished loading doesn't burn the one shot.
  if (sent) seenDemoTouches.add(key)
  return sent
}

/** Test seam: forget which demo touches were sent. */
export function resetDemoTouchesForTests() {
  seenDemoTouches.clear()
}
